import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { useRole } from '@/hooks/useRole'
import { FullPageSpinner } from '@/components/Spinner'
import { takeConsentReturn } from '@/lib/oauthConsent'

/** Entry point — resolves tenant and redirects to the correct route. */
export function RootIndex() {
  const { authState, signOut } = useAuth()
  const { status, userRole, tenantConfig, setupRequired, refresh, noRecord } = useRole()
  const navigate = useNavigate()

  const authenticated = authState.status === 'authenticated'

  useEffect(() => {
    if (authState.status === 'unauthenticated') {
      navigate('/login', { replace: true })
      return
    }

    // NOTE: a role-detection error while a valid session exists is NOT a
    // sign-out. It used to redirect to /login, which read as "the app forgot
    // me" whenever a fresh tab hit a transient tenant-config failure. The
    // signed-in error case now renders a retry UI below instead.
    if (status !== 'resolved') return

    // Every sign-in path lands here; an assistant connection interrupted by it resumes first.
    const consent = takeConsentReturn()
    if (consent) {
      navigate(consent, { replace: true })
      return
    }

    // Operators start the day on the calendar — it is the first screen they see
    // after signing in, every time. Chat and everything else is one click away
    // in the nav rail. (Setup still wins for a fresh tenant.)
    if (userRole === 'owner' || userRole === 'staff') {
      navigate(setupRequired ? '/setup' : '/schedule', { replace: true })
      return
    }

    // Customers land on the branded slug chat when available (unified path)
    if (userRole === 'customer') {
      navigate(tenantConfig?.slug ? `/${tenantConfig.slug}` : '/portal', { replace: true })
      return
    }

    navigate('/login', { replace: true })
  }, [status, userRole, tenantConfig, setupRequired, authState.status, navigate])

  // Retrying cannot fix this one: the business holds the client under another email.
  if (authenticated && status === 'no_record') {
    const business = noRecord?.businessName ?? 'the clinic'
    return (
      <div className="flex min-h-screen flex-1 flex-col items-center justify-center gap-4 bg-slate-900 px-6">
        <p className="text-center text-[40px]">🔎</p>
        <p className="text-center text-[17px] font-semibold text-slate-50">We can’t find your bookings yet</p>
        <p className="max-w-[340px] text-center text-sm leading-5 text-slate-400">
          You’re signed in as <span className="font-semibold text-slate-200">{noRecord?.email ?? 'this email'}</span>,
          but {business} doesn’t have it on your client record. Sign in with the email you gave them when you
          booked, or ask {business} to add this one — then sign in again.
        </p>
        <button
          type="button"
          className="mt-2 rounded-xl bg-blue-500 px-8 py-3"
          onClick={() => { void signOut() }}
        >
          <span className="text-base font-semibold text-white">Sign out</span>
        </button>
        <button type="button" className="py-1" onClick={() => { refresh() }}>
          <span className="text-[13px] text-slate-400 underline">I’ve been added — check again</span>
        </button>
      </div>
    )
  }

  if (authenticated && status === 'error') {
    return (
      <div className="flex min-h-screen flex-1 flex-col items-center justify-center gap-4 bg-slate-900 px-6">
        <p className="text-center text-[40px]">📶</p>
        <p className="text-center text-[17px] font-semibold text-slate-50">Couldn’t reach the server</p>
        <p className="max-w-[320px] text-center text-sm leading-5 text-slate-400">
          You’re still signed in — this is a connection problem, not a sign-out.
        </p>
        <button
          type="button"
          className="mt-2 rounded-xl bg-blue-500 px-8 py-3"
          onClick={() => { refresh() }}
        >
          <span className="text-base font-semibold text-white">Try again</span>
        </button>
      </div>
    )
  }

  return <FullPageSpinner />
}
